import { ExportButton } from '@/common/buttons/ExportButton';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Explains the match column's icons; the arrows point at the Contract that carries the value
const MatchLegend = ({ leftCtrt, rightCtrt }) => {
  const items = [
    { icon: fas.faCheck, color: 'green', label: 'Match' },
    { icon: fas.faTimes, color: 'red', label: 'Differs' },
    { icon: fas.faArrowLeft, color: 'orange', label: `Only in ${leftCtrt?.code ?? 'Contract A'}` },
    { icon: fas.faArrowRight, color: 'orange', label: `Only in ${rightCtrt?.code ?? 'Contract B'}` },
  ];

  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      {items.map((item) => (
        <Stack key={item.label} direction="row" spacing={0.5} alignItems="center">
          <FontAwesomeIcon icon={item.icon} style={{ color: item.color }} />
          <Typography variant="caption" color="text.secondary">
            {item.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

export const CtrtCompare = () => {
  const { navState } = useNavState();
  const rsrc = useRsrc();
  const gridRef = useRef(null);

  const [ctrts, setCtrts] = useState([]);

  const ctrtIds = useMemo(() => {
    if (Array.isArray(navState?.ctrtIds) && navState.ctrtIds.length > 0) {
      return navState.ctrtIds;
    }
    if (Array.isArray(navState?.ctrts) && navState.ctrts.length > 0) {
      return navState.ctrts.map((ctrt) => ctrt.id).filter(Boolean);
    }
    return [];
  }, [navState?.ctrtIds, navState?.ctrts]);

  const ctrtKey = useMemo(() => ctrtIds.join('|'), [ctrtIds]);

  useEffect(() => {
    if (ctrtIds.length !== 2) {
      setCtrts([]);
      return;
    }

    const rowsReq = { expr: ExprUtils.whereParams('id', ctrtIds) };
    rsrc.rows('md', 'CTRT', rowsReq).then((ctrtRows) => {
      const sortedCtrts = new Array(ctrtIds.length);
      ctrtRows.forEach((ctrt) => {
        const index = ctrtIds.indexOf(ctrt.id);
        if (index >= 0) {
          sortedCtrts[index] = ctrt;
        }
      });
      setCtrts(sortedCtrts.filter(Boolean));
    });
  }, [ctrtKey]);

  const augFieldDefs = useCallback(
    (fieldToFieldDef) => {
      if (ctrts.length !== 2) {
        return;
      }

      ObjUtils.clearObj(fieldToFieldDef);

      const [leftCtrt, rightCtrt] = ctrts;
      fieldToFieldDef.name = { field: 'name', autoGrouping: true };

      fieldToFieldDef.match = {
        field: 'match',
        width: 70,
        cellClass: 'ag-center-cell',
        type: 'MATCH',
      };

      // Each Contract's value column links to that Contract's own Entity
      fieldToFieldDef.ctrt0 = {
        field: 'ctrt0',
        displayName: (leftCtrt?.code ?? 'Contract A') + ' Values',
        children: [
          {
            displayName: 'Value',
            field: 'value0',
            type: 'ENTITY_LINK',
            module: 'md',
            linkEntityField: 'linkEntity0',
            linkEntityIdField: 'linkEntityId0',
          },
        ],
      };

      fieldToFieldDef.ctrt1 = {
        field: 'ctrt1',
        displayName: (rightCtrt?.code ?? 'Contract B') + ' Values',
        children: [
          {
            displayName: 'Value',
            field: 'value1',
            type: 'ENTITY_LINK',
            module: 'md',
            linkEntityField: 'linkEntity1',
            linkEntityIdField: 'linkEntityId1',
          },
        ],
      };
    },
    [ctrts],
  );

  const clientSideRowData = useCallback(
    (setDataSource) => {
      const reqParam = {
        ctrtId: ctrtIds[0],
        ctrtIds: ctrtIds,
      };
      rsrc.data('md', 'CTRT_COMPARE_DATA_PLUGIN', reqParam).then((res) => {
        setDataSource(res);
      });
    },
    [ctrtKey],
  );

  const handleExpandAll = () => {
    gridRef.current?.gridRef?.current?.gridApiRef?.current?.expandAll();
  };

  const handleCollapseAll = () => {
    gridRef.current?.gridRef?.current?.gridApiRef?.current?.collapseAll();
  };

  // The compare grid is client side, so it never builds an export request of its own
  const exportReq = useMemo(
    () => ({
      timeZoneName: DateUtils.timeZoneName(),
      displayName: 'Contract Compare',
      module: 'md',
      entity: 'CTRT',
      ctrtIds,
      uiType: 'CTRT_COMPARE',
    }),
    [ctrtKey],
  );

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', width: '100%' }}
    >
      <Box
        sx={{
          key: ctrts.length,
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Title icon={fas.faBalanceScale} title="Contract Compare" />
        {ctrts.length === 2 && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <MatchLegend leftCtrt={ctrts[0]} rightCtrt={ctrts[1]} />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button variant="outlined" size="small" onClick={handleExpandAll}>
                Expand
              </Button>
              <Button variant="outlined" size="small" onClick={handleCollapseAll}>
                Collapse
              </Button>
              <ExportButton entityName="Contract Compare" exportReq={exportReq} />
            </Box>
          </Box>
        )}
      </Box>
      {ctrts.length === 2 && (
        <EntityList
          ref={gridRef}
          module="md"
          entity="CTRT"
          showTitle={false}
          augFieldDefs={augFieldDefs}
          clientSideRowData={clientSideRowData}
          gridOptions={{
            treeData: true,
            defaultExpandLevel: 0,
          }}
        />
      )}
    </Box>
  );
};
